import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import { Download, Printer, ArrowLeft, Wifi, Waves, Home, MapPin, Sparkles } from "lucide-react";

const DEFAULT_URL = "https://grandview.elitestrhostingco.com/guide";

export default function Poster() {
  const [url, setUrl] = useState(DEFAULT_URL);
  const svgRef = useRef(null);

  function downloadPNG() {
    const canvas = document.querySelector('canvas[data-testid="poster-qr-canvas"]');
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = "grandview-estate-qr.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  }
  function downloadSVG() {
    const svg = svgRef.current;
    if (!svg) return;
    const data = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([data], { type: "image/svg+xml;charset=utf-8" });
    const link = document.createElement("a");
    link.download = "grandview-estate-qr.svg";
    link.href = URL.createObjectURL(blob);
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  }

  return (
    <div className="min-h-screen bg-[#F1E8DA]/60">
      {/* Toolbar (hidden when printing) */}
      <div className="print:hidden sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-[#0E2A47]/8">
        <div className="max-w-4xl mx-auto px-6 py-4 flex flex-wrap items-center gap-3">
          <Link to="/admin" data-testid="poster-back" className="inline-flex items-center gap-1.5 text-sm text-[#0E2A47]/70 hover:text-[#0E2A47] font-body"><ArrowLeft size={14} /> Owner</Link>
          <span className="text-[#0E2A47]/25">·</span>
          <div className="font-display font-medium text-[#0E2A47]">QR welcome poster</div>

          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <label className="text-xs uppercase tracking-widest text-[#0E2A47]/50 font-body">Encoded URL</label>
            <input
              data-testid="poster-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-72 rounded-full border border-[#0E2A47]/12 bg-white px-4 py-2 text-sm font-body text-[#0E2A47] outline-none focus:border-[#1FB6C9]"
            />
            <button data-testid="poster-download-png" onClick={downloadPNG} className="inline-flex items-center gap-1.5 rounded-full bg-[#0E2A47] text-white text-sm px-4 py-2 font-body"><Download size={14} /> PNG</button>
            <button data-testid="poster-download-svg" onClick={downloadSVG} className="inline-flex items-center gap-1.5 rounded-full border border-[#0E2A47]/15 bg-white text-[#0E2A47] text-sm px-4 py-2 font-body"><Download size={14} /> SVG</button>
            <button data-testid="poster-print" onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-full bg-[#1FB6C9] text-white text-sm px-4 py-2 font-body"><Printer size={14} /> Print</button>
          </div>
        </div>
      </div>

      {/* Printable sheet — 8.5×11 US Letter */}
      <div className="max-w-4xl mx-auto py-10 print:py-0 px-6 print:px-0">
        <div
          data-testid="poster-sheet"
          className="poster-sheet mx-auto bg-white shadow-2xl print:shadow-none"
          style={{ width: "8.5in", minHeight: "11in", padding: "0.6in 0.6in" }}
        >
          {/* Header */}
          <div className="text-center border-b border-[#0E2A47]/10 pb-6">
            <p className="text-xs uppercase tracking-[0.25em] text-[#1FB6C9] font-body">Welcome to</p>
            <h1 className="mt-2 text-[54px] leading-none text-[#0E2A47]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Grandview Estate</h1>
            <p className="mt-3 text-[#0E2A47]/70 font-body text-lg">Managed by Elite STR Hosting Co. · Canyon Lake, Texas</p>
          </div>

          {/* QR block */}
          <div className="mt-10 flex items-center justify-center gap-10">
            <div className="bg-white p-6 border-2 border-[#0E2A47]/10 rounded-2xl">
              <QRCodeCanvas
                data-testid="poster-qr-canvas"
                value={url}
                size={380}
                bgColor="#ffffff"
                fgColor="#0E2A47"
                level="H"
                includeMargin={false}
              />
              {/* hidden SVG mirror for download */}
              <div style={{ position: "absolute", left: "-9999px" }}>
                <QRCodeSVG ref={svgRef} value={url} size={800} bgColor="#ffffff" fgColor="#0E2A47" level="H" />
              </div>
            </div>
            <div className="max-w-[280px]">
              <p className="text-xs uppercase tracking-[0.25em] text-[#0E2A47]/60 font-body">Scan for everything</p>
              <p className="mt-4 text-[#0E2A47] font-body text-lg leading-relaxed">
                Wi-Fi · Pool &amp; Spa · House Info · Local Favorites · Checkout · Help
              </p>
              <p className="mt-6 text-sm text-[#0E2A47]/60 font-body">Or type this into your browser:</p>
              <p className="mt-1 text-sm font-mono text-[#0E2A47] break-all">{url.replace(/^https?:\/\//, "")}</p>
            </div>
          </div>

          {/* Feature strip */}
          <div className="mt-12 grid grid-cols-4 gap-4 pt-8 border-t border-[#0E2A47]/10">
            {[
              { icon: Wifi, label: "Wi-Fi & entry codes" },
              { icon: Waves, label: "Pool & hot tub" },
              { icon: Home, label: "House info & rules" },
              { icon: MapPin, label: "Local favorites" },
            ].map((f) => (
              <div key={f.label} className="text-center">
                <div className="mx-auto h-11 w-11 rounded-full bg-[#F1E8DA] flex items-center justify-center">
                  <f.icon size={20} className="text-[#0E2A47]" />
                </div>
                <div className="mt-3 text-xs text-[#0E2A47]/70 font-body">{f.label}</div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-14 text-center">
            <div className="inline-flex items-center gap-2 text-[#1FB6C9] font-body text-xs uppercase tracking-[0.25em]">
              <Sparkles size={14} /> AI concierge included <Sparkles size={14} />
            </div>
            <p className="mt-3 text-[#0E2A47]/70 font-body text-sm">Ask the guide anything — from hot-tub controls to dinner recommendations.</p>
            <p className="mt-8 text-xs text-[#0E2A47]/50 font-body">Emergencies: call 911 · Host: (210) 274-3556 · elitestrhostingco.com</p>
          </div>
        </div>
      </div>

      <style>{`
        @page { size: letter; margin: 0; }
        @media print {
          body, html { background: #ffffff !important; }
          .poster-sheet { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
